import React, { useState } from 'react'
import PropTypes from 'prop-types'

import IntegrationCard from './IntegrationCard'

// Compressed single-line status of connected transcript sources, expandable
// to the full IntegrationCard for connect/disconnect.
//
// Generic over providers: takes the full provider list and the user's
// integrations and figures out connection status per provider.
const IntegrationStatusBar = ({
  providers,
  integrations,
  isLoading,
  onConnect,
  onDisconnect,
  onStartOAuth,
  isConnecting,
  isDisconnecting,
  connectError,
  onResetConnectError
}) => {
  const [expanded, setExpanded] = useState(false)

  const findIntegration = providerKey =>
    integrations.find(i => i.provider === providerKey) || null

  const connectedProviders = providers.filter(p =>
    findIntegration(p.key)
  )

  const buildSummary = () => {
    if (isLoading) return 'Loading transcript sources…'
    if (providers.length === 0) {
      return 'No transcript sources available yet.'
    }
    if (connectedProviders.length === 0) {
      return 'No transcript sources connected'
    }
    const labels = connectedProviders.map(p => {
      const integ = findIntegration(p.key)
      const email =
        integ && integ.metadata && integ.metadata.accountEmail
      return email ? `${p.name} (${email})` : p.name
    })
    return `Connected: ${labels.join(', ')}`
  }

  return (
    <div
      className="border rounded mt-30"
      style={{ overflow: 'hidden' }}
    >
      <div
        className="d-flex justify-content-between align-items-center px-20 py-15 bg-gray-light"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded(prev => !prev)}
      >
        <p
          className="mb-0 text-gray-dark"
          style={{ fontSize: 14 }}
        >
          {connectedProviders.length > 0 && (
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#34C77B',
                marginRight: 8,
                verticalAlign: 'middle'
              }}
            />
          )}
          {buildSummary()}
        </p>
        <span
          className="text-gray-dark"
          style={{
            fontSize: 18,
            display: 'inline-block',
            transform: expanded ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.15s ease'
          }}
        >
          ›
        </span>
      </div>

      {expanded && (
        <div className="p-20">
          {providers.map(provider => (
            <IntegrationCard
              key={provider.key}
              provider={provider}
              integration={findIntegration(provider.key)}
              onConnect={onConnect}
              onDisconnect={onDisconnect}
              onStartOAuth={onStartOAuth}
              isConnecting={isConnecting}
              isDisconnecting={isDisconnecting}
              connectError={connectError}
              onResetConnectError={onResetConnectError}
            />
          ))}
        </div>
      )}
    </div>
  )
}

IntegrationStatusBar.propTypes = {
  providers: PropTypes.array.isRequired,
  integrations: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onConnect: PropTypes.func.isRequired,
  onDisconnect: PropTypes.func.isRequired,
  onStartOAuth: PropTypes.func,
  isConnecting: PropTypes.bool,
  isDisconnecting: PropTypes.bool,
  connectError: PropTypes.object,
  onResetConnectError: PropTypes.func
}

export default IntegrationStatusBar
